import { ParsedUrlQuery } from 'querystring';

import { useEffect, useState } from 'react';

import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';

import {
  formatMomentDate,
  getNDaysLater,
  FLIGHT_SEARCH_DATE_FORMAT,
} from '@utils/dateUtil';
import { ProfileReservation, DownArrowIcon } from '@assets/icons/';
import CustomIcon from '@components/utils/CustomIcon';
import { useSkipEffectOnMount } from '@hooks/lifecycle';
import { paramsToObject } from '@components/views/utils';

import SearchBarInput from '../../SearchBarInput';
import { SearchBarDateRangePickerInputProps } from './SearchBarDateRangePickerInputInterface';

const DateRangePicker = dynamic(() =>
  import('@components/DatePicker').then((module) => module.DateRangePicker)
);
const SearchBarDateRangeInput = ({
  dateProps: { startDate, endDate, onDatesChange, ...otherDateProps },
  error,
  setButtonDisable,
  isBookerSearch = false, // used to not use react-tiny-popover (specific to the booker component)
  isFlight = false,
  setFlightFocusedInput,
  isHoliday,
  initialData,
  setDefaultEndDate,
  setDefaultStarDate,
  ...otherProps
}: SearchBarDateRangePickerInputProps) => {
  const startDateString = startDate?.isValid()
    ? startDate.utc().local().format('DD.MM.YYYY') // UTC'yi önce UTC'ye çevir, sonra local() ile yerel saate al
    : '';

  const endDateFormatString = endDate?.isValid()
    ? endDate.utc().local().format('DD.MM.YYYY') // UTC'yi önce UTC'ye çevir, sonra local() ile yerel saate al
    : '';

  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = paramsToObject(searchParams?.entries());

  const [focusedInput, setFocusedInput] = useState<
    'startDate' | 'endDate' | null
  >('startDate');

  useEffect(() => {
    if (focusedInput === 'endDate' && startDate) {
      onDatesChange({
        startDate,
        endDate: null,
      });
    }
  }, [focusedInput, startDate]);
  useSkipEffectOnMount(() => {
    if (isFlight) {
      if (startDateString && endDateFormatString) {
        const urlstart = formatMomentDate(startDate, FLIGHT_SEARCH_DATE_FORMAT);
        const urlend = formatMomentDate(endDate, FLIGHT_SEARCH_DATE_FORMAT);
        if (!query.rdate) {
          //If flight is one way
          setButtonDisable?.(true);
        } else if (urlstart !== query.rdate && urlend !== query.ddate) {
          //If flight is rounded-trip
          setButtonDisable?.(true);
        }
      }
    } else {
      if (startDateString && endDateFormatString) {
        setButtonDisable?.(true);
      }
    }
    setDefaultEndDate?.(endDateFormatString);
    setDefaultStarDate?.(startDateString);
  }, [startDateString, endDateFormatString]);

  return (
    <SearchBarInput
      leftIcon={() => (
        <CustomIcon
          fontSize={28}
          paletteColor='coral'
          icon={ProfileReservation}
        />
      )}
      rightIcon={() => (
        <CustomIcon
          icon={DownArrowIcon}
          fontSize={16}
          paletteColor='darkGray'
        />
      )}
      isBookerSearch={isBookerSearch}
      isFilled={startDateString.length > 0 || endDateFormatString.length > 0}
      InputComponent={
        startDateString || endDateFormatString
          ? `${startDateString} - ${endDateFormatString}`
          : otherProps.placeholder
      }
      renderOverlayContent={(closePopup) => {
        return (
          <DateRangePicker
            initialData={initialData}
            onOutsideClick={() => {
              if (startDate && !endDate) {
                setFocusedInput('startDate');
                if (isFlight) {
                  setFlightFocusedInput?.('startDate');
                }
                onDatesChange({
                  startDate,
                  endDate: startDate ? getNDaysLater(startDate, 1) : null,
                });
              }
            }}
            isHoliday={isHoliday}
            focusedInput={focusedInput}
            onFocusChange={(focusedInput) => {
              if (focusedInput === null) {
                closePopup();
              }
              setFocusedInput(focusedInput || 'startDate');
              if (isFlight) {
                setFlightFocusedInput?.(focusedInput || 'startDate');
              }
            }}
            startDate={startDate}
            endDate={endDate}
            onDatesChange={onDatesChange}
            {...otherDateProps}
          />
        );
      }}
      error={error}
      {...otherProps}
    />
  );
};

export default SearchBarDateRangeInput;
